/**
 * Sentry Error Tracking — EU Data Residency
 *
 * Cardinal Rules enforced:
 * - Rule #3 (EU data): DSN must point to de.sentry.io for Frankfurt data residency
 * - Rule #4 (no raw trace data in logs): beforeSend scrubs PII via piiScrubber
 *
 * Required env vars:
 *   VITE_SENTRY_DSN        — Sentry DSN (de.sentry.io for EU hosting)
 *   VITE_SENTRY_ENVIRONMENT — "production" | "staging" | "development"
 *
 * Build-time vars (for source map upload via @sentry/vite-plugin in vite.config.ts):
 *   SENTRY_AUTH_TOKEN, SENTRY_ORG, SENTRY_PROJECT
 */

import * as Sentry from '@sentry/react';
import { ENV, isDevelopment } from '../config/env';
import { scrubPayload } from '../utils/piiScrubber';

export function initSentry(): void {
  // Only initialize if a DSN is configured
  if (!ENV.SENTRY_DSN) return;

  Sentry.init({
    dsn: ENV.SENTRY_DSN,
    environment: ENV.SENTRY_ENVIRONMENT,

    // Only capture errors in production — avoid noise in dev
    enabled: !isDevelopment() || !!import.meta.env.VITE_SENTRY_FORCE_ENABLE,

    // Sample rate: 100% errors in production
    sampleRate: 1.0,

    // No performance tracing (keeps payload minimal, no user session data)
    tracesSampleRate: 0,

    // Cardinal Rule #4: never send raw trace payloads to Sentry
    beforeSend(event) {
      // Scrub PII from any extra data attached to the event
      if (event.extra) {
        event.extra = scrubPayload(event.extra) as Record<string, unknown>;
      }

      // Scrub request body if present
      if (event.request?.data) {
        event.request.data = scrubPayload(event.request.data);
      }

      // Strip user-identifying fields (keep only anonymous session context)
      // Cardinal Rule #3: no PII to external services
      if (event.user) {
        const safeUser: Sentry.User = {};
        // Preserve only the anonymous session id, drop email/ip/username
        if (event.user.id) safeUser.id = event.user.id;
        event.user = safeUser;
      }

      return event;
    },

    // Ignore noisy browser extensions and third-party errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Loading chunk \d+ failed/,
    ],
  });
}

/**
 * Report a caught error to Sentry with optional context.
 * Safe to call even if Sentry is not initialized.
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (!ENV.SENTRY_DSN) return;
  Sentry.captureException(error, context ? { extra: scrubPayload(context) } : undefined);
}
